/**
 * Lander Page (Host-Based Routing)
 * P20250925-125 — Lease-first domain landers
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  getDomainBySlug,
  getSimilarDomains,
  priceBand,
  type InventoryRecord,
} from '@/lib/inventory/csv';
import { getPersuasionForSlug, type PersuasionRecord } from '@/lib/persuasion/csv';
import { leaseFromBIN } from '@/lib/lease';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, ShieldCheck, Zap, TrendingUp } from 'lucide-react';
import { fireEvent } from '@/lib/analytics';
import { addToCart } from '@/lib/cartStore';
import { toast } from 'sonner';
import { useParallaxRef } from '@/hooks/useParallax';

export default function Lander() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [domain, setDomain] = useState<InventoryRecord | null>(null);
  const [ideas, setIdeas] = useState<PersuasionRecord[]>([]);
  const [similar, setSimilar] = useState<InventoryRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    const load = async () => {
      setLoading(true);

      const domainData = await getDomainBySlug(slug);
      if (!domainData) {
        setLoading(false);
        return;
      }

      setDomain(domainData);

      // Load persuasion ideas
      const ideasData = await getPersuasionForSlug(slug);
      setIdeas(ideasData.slice(0, 3)); // Top 3

      // Load similar domains
      const similarData = await getSimilarDomains(domainData, 6);
      setSimilar(similarData);

      setLoading(false);

      // Analytics
      fireEvent('lander_view', {
        tld: domainData.tld,
        priceBand: priceBand(domainData),
      });
    };

    load();
  }, [slug]);

  const handleCTA = async (action: 'lease_now' | 'buy_now' | 'offer' | 'apply' | 'explore') => {
    fireEvent('cta_click', { action });

    if (action === 'lease_now' && domain) {
      const added = addToCart({
        domain: domain.domain,
        tld: domain.tld,
        binPrice: domain.bin_usd,
        lease: true,
        leaseTerm: domain.lease_term_months,
        leaseMonthly: leaseFromBIN(domain.bin_usd, domain.lease_term_months).monthly,
      });

      if (added) {
        toast.success('Added to cart!');
        navigate('/cart');
      } else {
        toast.error('Already in cart');
      }
    } else if (action === 'buy_now' && domain) {
      const added = addToCart({
        domain: domain.domain,
        tld: domain.tld,
        binPrice: domain.bin_usd,
        lease: false,
      });

      if (added) {
        toast.success('Added to cart!');
        navigate('/cart');
      } else {
        toast.error('Already in cart');
      }
    } else if (action === 'offer') {
      navigate('/offer');
    } else if (action === 'apply') {
      navigate('/apply');
    } else if (action === 'explore') {
      navigate('/domains');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading domain...</p>
        </div>
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-4xl font-bold mb-4">Domain Not Found</h1>
          <p className="text-muted-foreground mb-8">
            This domain is not currently available in our inventory.
          </p>
          <Button onClick={() => handleCTA('explore')} size="lg">
            Explore Available Domains
          </Button>
        </div>
      </div>
    );
  }

  const lease = leaseFromBIN(domain.bin_usd, domain.lease_term_months);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 60 });
  const heroCtaRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 40 });
  const ideasGridRef = useParallaxRef({ speed: 0.06, axis: 'y', maxTranslate: 44 });
  const similarGridRef = useParallaxRef({ speed: 0.06, axis: 'y', maxTranslate: 44 });

  return (
    <>
      <Helmet>
        <title>{domain.domain} - Premium Domain For Sale</title>
        <meta
          name="description"
          content={`Get ${domain.domain} - ${lease.copy} lease or $${domain.bin_usd.toLocaleString()} to buy. Secure transfer, ownership protection.`}
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="border-b bg-gradient-to-b from-background to-muted/20 stagger-fade-1">
          <div className="container mx-auto px-4 py-16 md:py-24">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <div className="space-y-4">
                <Badge variant="secondary" className="mb-2">
                  Premium {domain.tld.toUpperCase()} Domain
                </Badge>
                <h1
                  ref={heroTitleRef as React.RefObject<HTMLHeadingElement>}
                  className="text-4xl md:text-6xl font-bold tracking-tight"
                >
                  {domain.domain}
                </h1>
              </div>

              <div
                ref={heroCtaRef as React.RefObject<HTMLDivElement>}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <Button
                  onClick={() => handleCTA('lease_now')}
                  size="lg"
                  className="text-lg px-8 h-14 w-full sm:w-auto"
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  Lease Now — {lease.copy}
                </Button>

                <Button
                  onClick={() => handleCTA('buy_now')}
                  variant="outline"
                  size="lg"
                  className="apple-button-secondary-cta text-lg px-8 h-14 w-full sm:w-auto"
                >
                  Buy Now — ${domain.bin_usd.toLocaleString()}
                </Button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                <Button
                  onClick={() => handleCTA('offer')}
                  variant="ghost"
                  size="sm"
                  className="apple-button-secondary-cta"
                >
                  Make an Offer
                </Button>
                <span className="text-muted-foreground">•</span>
                <Button
                  onClick={() => handleCTA('apply')}
                  variant="ghost"
                  size="sm"
                  className="apple-button-secondary-cta"
                >
                  Apply for Free Use
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Ideas Section */}
        {ideas.length > 0 && (
          <section className="py-16 md:py-24 stagger-fade-2">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-center mb-12">Perfect For</h2>

                <div
                  ref={ideasGridRef as React.RefObject<HTMLDivElement>}
                  className="grid md:grid-cols-3 gap-6"
                >
                  {ideas.map((idea, index) => (
                    <Card
                      key={idea.idea_id}
                      className="apple-card animate-fade-in-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <CardHeader>
                        <CardTitle className="text-xl">{idea.business_idea}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">{idea.elevator_pitch}</p>
                        <div className="pt-2 border-t">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            Target Market
                          </p>
                          <p className="text-sm">{idea.target_market}</p>
                        </div>
                        {idea.proof_points && (
                          <div className="pt-2 border-t">
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Potential
                            </p>
                            <p className="text-sm">{idea.proof_points}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Brandability Stats */}
        <section className="py-16 bg-muted/30 stagger-fade-3">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-center mb-8">Brandability Score</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">{domain.domain.length}</div>
                  <div className="text-sm text-muted-foreground">Characters</div>
                </div>

                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {domain.domain.split('.')[0].split(/[-_]/).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Words</div>
                </div>

                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">.{domain.tld}</div>
                  <div className="text-sm text-muted-foreground">Extension</div>
                </div>

                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {priceBand(domain) === 'under-5k'
                      ? 'Good'
                      : priceBand(domain) === '5k-10k'
                        ? 'Great'
                        : 'Premium'}
                  </div>
                  <div className="text-sm text-muted-foreground">Value Tier</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Similar Domains */}
        {similar.length > 0 && (
          <section className="py-16 md:py-24 stagger-fade-4">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4">More Domains Like This</h2>
                  <Button
                    onClick={() => handleCTA('explore')}
                    variant="outline"
                    className="apple-button-secondary-cta"
                  >
                    Explore All Domains
                  </Button>
                </div>

                <div
                  ref={similarGridRef as React.RefObject<HTMLDivElement>}
                  className="grid md:grid-cols-3 gap-6"
                >
                  {similar.map((sim, index) => {
                    const simLease = leaseFromBIN(sim.bin_usd, sim.lease_term_months);
                    return (
                      <Card
                        key={sim.slug}
                        className="apple-card cursor-pointer animate-fade-in-up"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        onClick={() => navigate(`/lander/${sim.slug}`)}
                      >
                        <CardHeader>
                          <CardTitle className="text-lg">{sim.domain}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="text-2xl font-bold">{simLease.copy}</div>
                          <div className="text-sm text-muted-foreground">
                            or ${sim.bin_usd.toLocaleString()} to buy
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="py-16 bg-muted/30 stagger-fade-4">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center space-y-6">
              <h2 className="text-3xl font-bold">Ready to Get Started?</h2>
              <p className="text-lg text-muted-foreground">
                Secure this domain today. Fast transfer, ownership protection guaranteed.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={() => handleCTA('lease_now')}
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  Lease Now — {lease.copy}
                </Button>
                <Button
                  onClick={() => handleCTA('buy_now')}
                  variant="outline"
                  size="lg"
                  className="apple-button-secondary-cta w-full sm:w-auto"
                >
                  Buy Now — ${domain.bin_usd.toLocaleString()}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
